import * as Sentry from '@sentry/vue'

export interface ApiResponse {
	data?: unknown
}

export const useRestApi = () => {
	const metadata = window._drivm_form || window._drivm_landing || window._drivm
	const apiUrl = metadata?.api_url
		? `${metadata.api_url}/api/widget/`
		: import.meta.env.VITE_APP_API_URL
	const headers: Record<string, string> = {
		'content-type': 'application/json',
		accept: 'application/json',
	}

	const handleResponse = async <T>(res: Response): Promise<T> => {
		if (res.ok) return res.json()

		if (res.status === 401) {
			window.location.reload()
			return Promise.reject()
		}

		const stackTrace = await res.body
			?.getReader()
			.read()
			.then(data => new TextDecoder().decode(data.value))

		if (stackTrace) {
			if (!import.meta.env.PROD) {
				// show in console error stack trace
				console.log(JSON.parse(stackTrace))
				throw JSON.parse(stackTrace)
			}

			Sentry.captureMessage(JSON.parse(stackTrace).message)
			throw JSON.parse(stackTrace)
		}

		throw await res.json()
	}

	return {
		get<T = ApiResponse>(url: string) {
			if (url[0] === '/') {
				url = url.slice(1)
			}

			return new Promise<T>((resolve, reject) => {
				fetch(apiUrl + url, { headers })
					.then(res => handleResponse<T>(res))
					.then(data => {
						resolve(data)
					})
					.catch(err => {
						reject(err)
					})
			})
		},

		post<T = ApiResponse>(url: string, data: Record<string, unknown>) {
			if (url[0] === '/') {
				url = url.slice(1)
			}

			return new Promise<T>((resolve, reject) => {
				fetch(apiUrl + url, { method: 'POST', headers, body: JSON.stringify(data) })
					.then(res => handleResponse<T>(res))
					.then(data => {
						resolve(data)
					})
					.catch(reject)
			})
		},

		put<T = ApiResponse>(url: string, data: Record<string, unknown>) {
			if (url[0] === '/') {
				url = url.slice(1)
			}

			return new Promise<T>((resolve, reject) => {
				fetch(apiUrl + url, {
					method: 'PUT',
					headers,
					body: JSON.stringify(data),
				})
					.then(res => handleResponse<T>(res))
					.then(data => {
						resolve(data)
					})
					.catch(reject)
			})
		},

		deleteApi(url: string) {
			if (url[0] === '/') {
				url = url.slice(1)
			}

			return new Promise((resolve, reject) => {
				fetch(apiUrl + url, { method: 'DELETE', headers })
					.then(handleResponse)
					.then(data => {
						resolve(data)
					})
					.catch(reject)
			})
		},

		postForm<T = ApiResponse>(url: string, data: FormData) {
			if (url[0] === '/') {
				url = url.slice(1)
			}

			return new Promise<T>((resolve, reject) => {
				fetch(apiUrl + url, {
					method: 'POST',
					headers: { accept: 'application/json' },
					body: data,
				})
					.then(res => handleResponse<T>(res))
					.then(data => {
						resolve(data)
					})
					.catch(reject)
			})
		},
	}
}
